import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { getRegisteredUsers } from "../../../apis/dashboardApi";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import dayjs from "dayjs";

const Registrations = () => {
  const [filter, setFilter] = useState("day");
  const [chartData, setChartData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  useEffect(() => {
    const fetchData = async () => {
      const params = {
        type: filter,
        startDate: dayjs(startDate).format("YYYY-MM-DD"),
        endDate: dayjs(endDate).format("YYYY-MM-DD"),
      };

      try {
        const response = await getRegisteredUsers(params);
        const users = processUserData(response?.result);
        const chart = processChartData(users, startDate, endDate);

        setTableData(users);
        setChartData(chart);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [filter, startDate, endDate]);

  const handleFilterChange = (e) => {
    setFilter(e.target.value);
    setDefaultDateRange(e.target.value);
  };

  const setDefaultDateRange = (filterType) => {
    const today = new Date();
    let start, end;
    switch (filterType) {
      case "week":
        start = dayjs(today).subtract(7, "day").toDate();
        end = today;
        break;
      case "month":
        start = dayjs(today).subtract(1, "month").toDate();
        end = today;
        break;
      case "year":
        start = dayjs(today).subtract(1, "year").toDate();
        end = today;
        break;
      default:
        start = end = today;
    }
    setStartDate(start);
    setEndDate(end || today);
  };

  const processUserData = (data) => {
    return data.flatMap(
      (item) =>
        item.usersDetails?.map((user) => ({
          id: user.id,
          fullName: `${user.firstName} ${user.lastName}`,
          email: user.email,
          role: user.role,
          createdAt: user.createdAt,
        })) || [],
    );
  };

  const processChartData = (users, start, end) => {
    let dateRange;

    switch (filter) {
      case "year":
        dateRange = Array.from(
          { length: dayjs(end).diff(dayjs(start), "month") + 1 },
          (_, i) => {
            const month = dayjs(start).add(i, "month");
            return {
              date: month.format("MMM-YY"),
              count: 0,
            };
          },
        );
        break;

      case "month":
        dateRange = Array.from(
          { length: dayjs(end).diff(dayjs(start), "day") + 1 },
          (_, i) => {
            const day = dayjs(start).add(i, "day");
            return {
              date: day.format("MM-DD"),
              count: 0,
            };
          },
        );
        break;

      case "week":
      case "day":
        dateRange = Array.from(
          { length: dayjs(end).diff(dayjs(start), "day") + 1 },
          (_, i) => {
            const day = dayjs(start).add(i, "day");
            return {
              date: day.format("YYYY-MM-DD"),
              count: 0,
            };
          },
        );
        break;

      default:
        dateRange = [];
    }

    users.forEach((user) => {
      const userDate =
        filter === "year"
          ? dayjs(user.createdAt).format("MMM-YY")
          : filter === "month"
            ? dayjs(user.createdAt).format("MM-DD")
            : dayjs(user.createdAt).format("YYYY-MM-DD");
      const entry = dateRange.find((d) => d.date === userDate);
      if (entry) {
        entry.count += 1;
      }
    });

    return dateRange;
  };

  const startIdx = (currentPage - 1) * pageSize;
  const endIdx = startIdx + pageSize;
  const paginatedData = tableData.slice(startIdx, endIdx);
  const totalPages = Math.ceil(tableData.length / pageSize);

  return (
    <div className="graph-section">
      <h4>Registrations by time period {filter}</h4>
      <div className="filter-section">
        <div className="select-period-wrapper">
          <label>Select Period: </label>
          <select
            onChange={handleFilterChange}
            value={filter}
            className="datepic"
          >
            <option value="day">Day</option>
            <option value="week">Week</option>
            <option value="month">Month</option>
            <option value="year">Year</option>
          </select>
        </div>

        <div className="date-picker-wrapper">
          <label>From: </label>
          <DatePicker
            selected={startDate}
            onChange={(date) => setStartDate(date)}
            selectsStart
            startDate={startDate}
            endDate={endDate}
            maxDate={new Date()}
            className="datepic"
          />
        </div>
        <div className="date-picker-wrapper">
          <label>To: </label>
          <DatePicker
            selected={endDate}
            onChange={(date) => setEndDate(date)}
            selectsEnd
            startDate={startDate}
            endDate={endDate}
            minDate={startDate}
            maxDate={new Date()}
            className="datepic"
          />
        </div>
      </div>

      <ResponsiveContainer
        width="100%"
        height={300}
        className="bar-chart-wrapper"
      >
        <BarChart
          data={chartData}
          margin={{ top: 5, right: 30, left: -25, bottom: 5 }}
        >
          <defs>
            <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--one-orange)" stopOpacity={1} />
              <stop offset="100%" stopColor="var(--one-red)" stopOpacity={1} />
            </linearGradient>
          </defs>

          <XAxis dataKey="date" />
          <YAxis />
          <Tooltip />
          <Bar dataKey="count" fill="url(#barGradient)" barSize={25} />
        </BarChart>
      </ResponsiveContainer>

      <div style={{ marginTop: "20px" }}>
        <h4>User Details</h4>
        <table className="styled-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((row) => (
              <tr key={row.id}>
                <td>{row.fullName}</td>
                <td>{row.email}</td>
                <td>{row.role}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="pagination">
          <div>
            Page:
            <input
              type="number"
              value={currentPage}
              onChange={(e) => setCurrentPage(Number(e.target.value))}
              min="1"
              max={totalPages}
            />{" "}
            of {totalPages}
          </div>
          <div>
            Page Size:
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="15">15</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Registrations;
